import {AfterViewInit, Component, Input, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {TrackPlayerState, TrackPoint} from "../service";
import {TrackPlayerService} from "../service/track-player.service";
import {Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {DatePipe} from "@angular/common";
import * as L from "leaflet";
import {GlobalService, version} from "../../../global.config";
import {ModalController} from "@ionic/angular";
import {MapService} from "../service/map.service";
import * as P from "leaflet";

@Component({
  selector: 'app-track-controle',
  templateUrl: './track-controle.component.html',
  styleUrls: ['./track-controle.component.scss'],
})
export class TrackControleComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() trackData: any;
  @Input() trackType: string = "";
  version: String = version;

  stops: any;
  map: L.Map | null = null;
  playerState: TrackPlayerState = {
    isPlaying: false,
    isPaused: false,
    currentIndex: 0,
    totalPoints: 0,
    currentPosition: null,
    progress: 0,
    elapsedTime: 0,
    totalTime: 0
  };
  isControlsCollapsed = false;
  hasTrack = false;
  currentSpeed = 1;
  showTrail = true;
  loopPlayback = false;
  mapInitialized = false;

  // Distance tracking properties
  totalDistance = 0; // Total distance in km
  currentDistance = 0; // Current distance traveled in km
  private trackPoints: TrackPoint[] = [];

  // Store stop markers for management
  private stopMarkers: L.Marker[] = [];
  private stopMarkersGroup: L.LayerGroup | null = null;

  private subscriptions: Subscription[] = [];
  private baseMaps: any = {
    [this.version === "finek" ? "Finek" : "Fleet"]: P.tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}', {
      maxZoom: 20,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
    }),
    OpenStreetMap: P.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      maxNativeZoom: 17,
      attribution: '© OpenStreetMap contributors'
    })
  };

  constructor(private trackPlayerService: TrackPlayerService,
              private translateService: TranslateService,
              private globalService: GlobalService,
              private modalController: ModalController,
              private mapService: MapService
  ) {}

  ngOnInit(): void {
    // console.log("trackType", this.trackType, "trackData", this.trackData);

    if (!this.trackData) {
      console.warn('No track data provided, closing modal');
      this.close();
      return;
    }

    this.loadTrackFromGpsData();
    this.subscribeToPlayerState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.trackData && !changes.trackData.firstChange) {
      this.loadTrackFromGpsData();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initializeMap();
      this.resizeMap();
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.trackPlayerService.stop();
    this.trackPlayerService.clearTrack();
    this.clearStopMarkers();

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private initializeMap(): void {
    try {
      const mapContainer = document.getElementById('trackPlayerMap');
      if (!mapContainer) {
        console.error('Map container not found');
        return;
      }

      this.map = L.map('trackPlayerMap').setView([51.505, -0.09], 13);

      this.baseMaps['OpenStreetMap'].addTo(this.map);

      L.control.layers(this.baseMaps).addTo(this.map);

      this.stopMarkersGroup = L.layerGroup().addTo(this.map);
      this.trackPlayerService.initialize(this.map);
      this.mapInitialized = true;

      if (this.trackData && this.trackData.length > 0) {
        this.loadTrack(this.trackData);
      }
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }

  private subscribeToPlayerState(): void {
    const stateSubscription = this.trackPlayerService.state$.subscribe(state => {
      this.playerState = state;
      this.hasTrack = state.totalPoints > 0;
      // Update current distance when player state changes
      this.updateCurrentDistance();
    });

    const positionSubscription = this.trackPlayerService.positionUpdate$.subscribe(position => {
      // console.log('Position update:', position);
      // Update current distance when position changes
      this.updateCurrentDistance();
    });

    const endSubscription = this.trackPlayerService.playbackEnd$.subscribe(() => {
      // console.log('Playback ended');
    });

    this.subscriptions.push(stateSubscription, positionSubscription, endSubscription);
  }

  loadTrack(trackPoints: TrackPoint[]): void {
    if (this.trackData.stops) {
      this.stops = this.trackData.stops;
    }
    if (!this.mapInitialized || !this.map) {
      console.warn('Map not initialized yet, storing track data');
      this.trackData = trackPoints;
      return;
    }

    // Store track points for distance calculations
    this.trackPoints = trackPoints;

    // Calculate total distance
    this.calculateTotalDistance();

    this.trackPlayerService.loadTrack(trackPoints, {
      speed: this.currentSpeed,
      showTrail: this.showTrail,
      trailColor: '#3388ff',
      trailWeight: 5,
      interpolate: true,
      loop: this.loopPlayback
    });

    if (this.trackType !== "path") {
      this.addStopMarkers();
    }

    this.fitMapToBounds(trackPoints);
  }

  /**
   * Calculate total distance of the track
   */
  private calculateTotalDistance(): void {
    if (this.trackPoints.length < 2) {
      this.totalDistance = 0;
      return;
    }

    let totalDistance = 0;
    for (let i = 0; i < this.trackPoints.length - 1; i++) {
      const point1 = this.trackPoints[i];
      const point2 = this.trackPoints[i + 1];
      totalDistance += this.calculateDistanceBetweenPoints(point1, point2);
    }

    this.totalDistance = totalDistance;
    // console.log(`Total track distance: ${this.totalDistance.toFixed(2)} km`);
  }

  /**
   * Update current distance based on player state
   */
  private updateCurrentDistance(): void {
    if (!this.trackPoints || this.trackPoints.length < 2 || this.playerState.currentIndex === 0) {
      this.currentDistance = 0;
      return;
    }

    let currentDistance = 0;
    const currentIndex = Math.min(this.playerState.currentIndex, this.trackPoints.length - 1);

    // Calculate distance up to current point
    for (let i = 0; i < currentIndex; i++) {
      const point1 = this.trackPoints[i];
      const point2 = this.trackPoints[i + 1];
      currentDistance += this.calculateDistanceBetweenPoints(point1, point2);
    }

    // If we have progress within the current segment, add partial distance
    if (currentIndex < this.trackPoints.length - 1) {
      const segmentProgress = this.playerState.progress / 100;
      const totalSegments = this.trackPoints.length - 1;
      const currentSegmentProgress = (segmentProgress * totalSegments) % 1;

      if (currentSegmentProgress > 0) {
        const point1 = this.trackPoints[currentIndex];
        const point2 = this.trackPoints[currentIndex + 1];
        const segmentDistance = this.calculateDistanceBetweenPoints(point1, point2);
        currentDistance += segmentDistance * currentSegmentProgress;
      }
    }

    this.currentDistance = currentDistance;
  }

  /**
   * Calculate distance between two points using Haversine formula
   * @param point1 First point
   * @param point2 Second point
   * @returns Distance in kilometers
   */
  private calculateDistanceBetweenPoints(point1: TrackPoint, point2: TrackPoint): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.deg2rad(point2.lat - point1.lat);
    const dLng = this.deg2rad(point2.lng - point1.lng);

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(point1.lat)) * Math.cos(this.deg2rad(point2.lat)) *
      Math.sin(dLng/2) * Math.sin(dLng/2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Format distance for display
   * @param distance Distance in kilometers
   * @returns Formatted distance string
   */
  formatDistance(distance: number): string {
    if (distance < 1) {
      return `${(distance * 1000).toFixed(0)}m`;
    } else {
      return `${distance.toFixed(2)}km`;
    }
  }

  private fitMapToBounds(trackPoints: TrackPoint[]): void {
    if (!this.map || trackPoints.length === 0) return;

    const bounds = L.latLngBounds([]);

    trackPoints.forEach(point => {
      bounds.extend([point.lat, point.lng]);
    });

    if (this.trackType !== "path" && this.trackData?.stops) {
      this.trackData.stops.forEach((stop: any) => {
        bounds.extend([stop.stopLatitude, stop.stopLongitude]);
      });
    }

    this.map.fitBounds(bounds, { padding: [50, 50] });
  }

  private addStopMarkers(): void {
    // console.log("this is stops matkers", this.stops);
    if (!this.stops || !this.map || !this.stopMarkersGroup) {
      return;
    }

    this.clearStopMarkers();

    const datePipe = new DatePipe('en-US');

    this.stops.forEach((stop: any, index: number) => {
      const marker = L.marker([stop.stopLatitude, stop.stopLongitude]);

      const formattedDate = datePipe.transform(stop.beginStopTime, "dd/MM/yyyy HH:mm:ss", null, 'en-US');
      const popup = `
        <div>
          <b>Date début d'arrêt:</b> ${formattedDate}<br>
          <b>Durée d'arrêt:</b> ${stop.stopDurationStr}
        </div>
      `;

      const stopIcon = L.icon({
        iconUrl: this.globalService.imagesDir + "stop_smal.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34]
      });

      marker.setIcon(stopIcon);
      marker.bindPopup(popup);

      marker.on('click', () => {
        if (this.map) {
          this.map.setView([stop.stopLatitude, stop.stopLongitude], 17);
        }
      });

      this.stopMarkersGroup.addLayer(marker);
      this.stopMarkers.push(marker);
    });

    // console.log(`Added ${this.stopMarkers.length} stop markers`);
  }

  private clearStopMarkers(): void {
    if (this.stopMarkersGroup) {
      this.stopMarkersGroup.clearLayers();
    }
    this.stopMarkers = [];
  }

  togglePlayback(): void {
    if (this.playerState.isPlaying) {
      this.trackPlayerService.pause();
    } else if (this.playerState.isPaused) {
      this.trackPlayerService.resume();
    } else {
      this.trackPlayerService.play();
    }
  }

  stop(): void {
    this.trackPlayerService.stop();
  }

  onSeek(event: Event): void {
    const target = event.target as HTMLInputElement;
    const percentage = parseFloat(target.value);
    this.trackPlayerService.seekTo(percentage);
  }

  onSpeedChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.currentSpeed = parseFloat(target.value);
    this.trackPlayerService.setSpeed(this.currentSpeed);
  }

  /**
   * Set speed directly to a specific value
   * @param speed The speed value to set
   */
  setSpeed(speed: number): void {
    this.currentSpeed = speed;
    this.trackPlayerService.setSpeed(this.currentSpeed);
  }

  onShowTrailChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.showTrail = target.checked;
    this.trackPlayerService.updateOptions({ showTrail: this.showTrail });
  }

  onLoopChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.loopPlayback = target.checked;
    this.trackPlayerService.updateOptions({ loop: this.loopPlayback });
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  transformGpsDataToTrackPoints(): TrackPoint[] {
    // console.log("this is track points", this.trackData);
    let trackPoints: TrackPoint[]
    if (this.trackType !== "mileage") {
      if (!this.trackData || !this.trackData.coordinates || !Array.isArray(this.trackData.coordinates)) {
        console.error('Invalid GPS data provided');
        return [];
      }
      trackPoints = this.trackData.coordinates.map((coord: any, index: number) => {
        return {
          lat: coord.lat,
          lng: coord.lng,
          timestamp: coord.date,
          speed: coord.speed,
          heading: this.calculateHeading(coord, this.trackData.coordinates[index + 1]),
          altitude: coord.altitude || 0,
          memsX: coord.memsX,
          memsY: coord.memsY,
          memsZ: coord.memsZ,
          aboveSpeedLimit: coord.aboveTheSpeedLimit,
          originalIndex: index
        };
      });
    } else {
      if (!this.trackData || !this.trackData.points || !Array.isArray(this.trackData.points)) {
        console.error('Invalid GPS data provided');
        return [];
      }
      trackPoints = this.trackData.points.map((coord: any, index: number) => {
        return {
          lat: coord.lat,
          lng: coord.lng,
          timestamp: coord.date,
          speed: coord.speed,
          heading: this.calculateHeading(coord, this.trackData.points[index + 1]),
          altitude: coord.altitude || 0,
          memsX: coord.memsX,
          memsY: coord.memsY,
          memsZ: coord.memsZ,
          aboveSpeedLimit: coord.aboveTheSpeedLimit,
          originalIndex: index
        };
      });
    }
    // console.log(`Transformed ${trackPoints.length} GPS coordinates to track points`);

    return trackPoints;
  }

  private calculateHeading(point1: any, point2: any): number {
    if (!point2) return 0;

    const lat1Rad = this.deg2rad(point1.lat);
    const lat2Rad = this.deg2rad(point2.lat);
    const deltaLngRad = this.deg2rad(point2.lng - point1.lng);

    const y = Math.sin(deltaLngRad) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLngRad);

    const headingRad = Math.atan2(y, x);
    const headingDeg = this.rad2deg(headingRad);

    return (headingDeg + 360) % 360;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  private rad2deg(rad: number): number {
    return rad * (180 / Math.PI);
  }

  loadTrackFromGpsData(): void {
    const trackPoints = this.transformGpsDataToTrackPoints();
    if (trackPoints.length > 0) {
      this.loadTrack(trackPoints);
    } else {
      console.warn('No valid track points found in GPS data');
    }
  }

  getTrackStatistics(gpsData: any): any {
    if (!gpsData) return null;

    return {
      deviceId: gpsData.deviceId,
      startTime: new Date(gpsData.beginPathTime),
      endTime: new Date(gpsData.endPathTime),
      duration: gpsData.pathDuration,
      durationFormatted: this.formatTime(gpsData.pathDuration),
      distance: gpsData.distanceDriven,
      distanceFormatted: (gpsData.distanceDriven * 1000).toFixed(0) + 'm',
      maxSpeed: gpsData.maxSpeed,
      avgSpeed: gpsData.pathDuration > 0 ?
        ((gpsData.distanceDriven / (gpsData.pathDuration / 3600)).toFixed(1)) : 0,
      pointCount: gpsData.coordinates ? gpsData.coordinates.length : 0,
      startLocation: {
        lat: gpsData.beginPathLatitude,
        lng: gpsData.beginPathLongitude
      },
      endLocation: {
        lat: gpsData.endPathLatitude,
        lng: gpsData.endPathLongitude
      },
      fuelUsed: gpsData.fuelUsed,
      odometer: {
        start: gpsData.startOdo,
        end: gpsData.endOdo,
        distance: gpsData.endOdo - gpsData.startOdo
      }
    };
  }

  resizeMap(): void {
    if (this.map) {
      setTimeout(() => {
        this.map!.invalidateSize();
      }, 100);
    }
  }

  getPlayButtonText(): string {
    if (this.playerState.isPlaying) {
      return this.translateService.instant('TRACK_PLAYER.pause');
    } else if (this.playerState.isPaused) {
      return this.translateService.instant('TRACK_PLAYER.resume');
    } else {
      return this.translateService.instant('TRACK_PLAYER.play');
    }
  }

  close() {
    this.modalController.dismiss();
  }

  /**
   * Toggle the collapse/expand state of the controls panel
   */
  toggleControls(): void {
    this.isControlsCollapsed = !this.isControlsCollapsed;

    // Resize map after the CSS transition completes
    setTimeout(() => {
      this.resizeMap();
    }, 350);
  }
}
